import React from "react";
import PropTypes from "prop-types";
import LollipopPetal from "../models/LollipopPetal";
import VariantLink from "../../../components/TooltipActions/VariantLink";
import Track from "../../../models/Track";
import { dbLabels } from "../../../constants/dbs";
import ReviewStars from "../../../components/ReviewStars";
import PathogenicityLabel from "../../../components/PathogenicityLabel";
import PublicationLinks from "../../../../../components/Publications/components/PublicationLinks";
import ImpactLabel from "../../../components/ImpactLabel/ImpactLabel";
import { pluralize } from "../../../../../common/helpers/helpers";

const rowCl =
  "tw-h-6 tw-flex tw-items-center tw-border-b border-normal tw-px-1";
const columnCl =
  "tw-min-w-16 tw-truncate tw-flex-shrink-0 tw-relative tw-flex-grow-333 tw-border-r border-normal";
const headerCl =
  "tw-flex tw-py-1 tw-items-center tw-border-b tw-border-t border-normal tw-px-1 tw-font-semibold tw-bg-blueGray-400 tw-bg-opacity-30 color-2";

function PetalInfo({ petal, actions, track }) {
  if (
    !track ||
    !petal ||
    !petal.pathogenicityDetails ||
    !petal.pathogenicityDetails[0] ||
    !petal.pathogenicityDetails[0].variant ||
    !petal.pathogenicityDetails[0].variant.id
  ) {
    return null;
  }

  const { pathogenicityDetails } = petal;
  const { variant, codingImpactIndex } = pathogenicityDetails[0];
  const state = track.controller.getState() || {};
  const { variantToPetalMap, pathColors, impactColors } = state;
  const petalMap = variantToPetalMap || new Map();
  const petals = petalMap.get(variant.id) || [];
  const sourceCounts = {};
  const sourcePathoCounts = {};
  const sourcePathoCountsPubs = {};
  const entryDict = {};

  const sources = petals
    .sort((a, b) => a.pathoIndex - b.pathoIndex)
    .map((x) => x.pathogenicityDetails)
    .reduce((a, i) => a.concat(i), [])
    .filter((x) => dbLabels[x.sourceIndex])
    .map((x) => {
      const { sourceIndex, pathoIndex, pubmedIds } = x;
      const sourceKey = sourceIndex;
      const entryKey = `${sourceIndex}-${pathoIndex}`;
      const sourceCount = sourceCounts[sourceKey] || 0;
      const sourcePathoCount = sourcePathoCounts[entryKey] || 0;
      const pubs = sourcePathoCountsPubs[entryKey] || new Set();
      const newPubSet = pubmedIds.reduce((a, i) => a.add(i), pubs);

      sourceCounts[sourceKey] = sourceCount + x.count;
      sourcePathoCounts[entryKey] = sourcePathoCount + x.count;
      sourcePathoCountsPubs[entryKey] = newPubSet;

      return { ...x, entryKey, sourceKey };
    })
    .map((x) => {
      const { sourceKey, entryKey } = x;
      const sourceCount = sourceCounts[sourceKey];
      const sourcePathoCount = sourcePathoCounts[entryKey];
      const pubSet = sourcePathoCountsPubs[entryKey];
      if (entryDict[entryKey]) return null;
      entryDict[entryKey] = true;
      return { ...x, pubSet, sourceCount, sourcePathoCount };
    })
    .filter(Boolean)
    .map((x) => ({
      ...x,
      pubmedIds: Array.from(x.pubSet),
      hasConflict: x.sourcePathoCount < x.sourceCount,
      hasPubs: x.pubmedIds.length > 0,
      name: dbLabels[x.sourceIndex],
    }));

  const hasAnyPubmed = sources.some((x) => x.hasPubs);

  return (
    <>
      <div className="tooltip-header border-normal">
        <div className="tooltip-title">
          <VariantLink variant={variant} />
        </div>
        <div className="tw-w-6" />
        {actions}
      </div>

      <div className="tooltip-row">
        <div className="tooltip-label tw-w-24">Coding Impact:</div>
        <ImpactLabel impactIndex={codingImpactIndex} colors={impactColors} />
      </div>

      <div className="tw-h-1" />

      <div className="tw-flex tw-border-l border-normal">
        <div className={columnCl}>
          <div className={headerCl}>Source</div>

          {sources.map((x) => (
            <div key={`${x.name}-${x.pathoIndex}`} className={rowCl}>
              <span className="tw-capitalize tw-font-normal">{x.name}</span>
              <div className="tw-w-2" />
              <ReviewStars stars={x.reviewStars} />
            </div>
          ))}
        </div>

        <div className={columnCl}>
          <div className={headerCl}>Submissions</div>

          {sources.map((x) => (
            <div key={`${x.name}-${x.pathoIndex}`} className={rowCl}>
              {x.hasConflict ? (
                <>
                  {x.sourcePathoCount} ouf of {x.sourceCount}
                </>
              ) : (
                x.sourcePathoCount
              )}
            </div>
          ))}
        </div>

        <div className={`${columnCl} tw-w-16`}>
          <div className={headerCl}>Verdict</div>

          {sources.map((x) => (
            <div key={`${x.name}-${x.pathoIndex}`} className={rowCl}>
              <PathogenicityLabel
                pathoIndex={x.pathoIndex}
                colors={pathColors}
                isLabelShort={true}
              />
            </div>
          ))}
        </div>

        {hasAnyPubmed ? (
          <div className={`${columnCl} tw-w-16`}>
            <div className={headerCl}>PubMed</div>
            {sources.map((x) => (
              <div key={`${x.name}-${x.pathoIndex}`} className={rowCl}>
                <PublicationLinks
                  ids={x.pubmedIds}
                  className="tw-text-blue-500 tw-cursor-pointer"
                >
                  {x?.pubmedIds?.length}{" "}
                  {pluralize(x?.pubmedIds?.length, "PMID", "PMIDs")}
                </PublicationLinks>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="tw-h-2" />
    </>
  );
}

PetalInfo.propTypes = {
  petal: PropTypes.instanceOf(LollipopPetal).isRequired,
  track: PropTypes.instanceOf(Track).isRequired,
  actions: PropTypes.node,
};

PetalInfo.defaultProps = {
  actions: null,
};

export default React.memo(PetalInfo);
